import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface DeviceInfos {
  device_id?: string
  platform?: string
  app_version?: string
  phone_model?: string
  real_ip?: string
  language?: string
}

interface DeviceInfosStore extends DeviceInfos {
  setDeviceInfos: (infos: Partial<DeviceInfos>) => void
  clearDeviceInfos: () => void
}

// Generate a random device ID
function generateDeviceId(): string {
  return (
    'web-' +
    Math.random().toString(36).substr(2, 9) +
    '-' +
    Date.now().toString(36)
  )
}

// Get device information for web environment
function getWebDeviceInfos(): DeviceInfos {
  const userAgent = navigator.userAgent
  const language = navigator.language || 'en'

  // Platform must be 'WEB' only per API spec
  const platform = 'WEB'

  // Detect device model (browser + OS)
  let phoneModel = 'Unknown Browser'
  if (userAgent.includes('Chrome')) {
    if (userAgent.includes('Mac')) phoneModel = 'Chrome MacOS'
    else if (userAgent.includes('Windows')) phoneModel = 'Chrome Windows'
    else if (userAgent.includes('Linux')) phoneModel = 'Chrome Linux'
    else phoneModel = 'Chrome'
  } else if (userAgent.includes('Firefox')) {
    if (userAgent.includes('Mac')) phoneModel = 'Firefox MacOS'
    else if (userAgent.includes('Windows')) phoneModel = 'Firefox Windows'
    else phoneModel = 'Firefox'
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    phoneModel = 'Safari MacOS'
  } else if (userAgent.includes('Edge')) {
    phoneModel = 'Edge Windows'
  }

  return {
    device_id: generateDeviceId(),
    platform,
    app_version: '1.1.0', // Version without -web suffix
    phone_model: phoneModel,
    real_ip: '', // IP will be fetched separately if needed
    language: language.split('-')[0], // Extract language code only (en, ru, etc.)
  }
}

export const useDeviceInfosStore = create<DeviceInfosStore>()(
  persist(
    (set) => ({
      // Initialize with web device information
      ...getWebDeviceInfos(),

      setDeviceInfos: (infos: Partial<DeviceInfos>) =>
        set((state) => ({
          ...state,
          ...infos,
        })),

      clearDeviceInfos: () => set(getWebDeviceInfos()),
    }),
    {
      name: 'device-infos',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name)
          return str ? JSON.parse(str) : null
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
)
